<template>
  <div>
    <ion-grid>
      <ion-row>
        <ion-col size="6">
          <h5>Bridge Summary</h5>

          <ion-list>
            <template v-if="bridge.cachedBridge">
              <ion-item>
                Location: {{ bridge.cachedBridge.location }}
              </ion-item>
            </template>

            <ion-item>
              Region: {{ region }}
            </ion-item>

            <ion-item>
              Cloud: {{ cloudRegion }}
            </ion-item>

            <ion-item v-if="bridge.commit">
              Version:
              <code>{{ bridge.commit.version }}</code>
              <ion-button
                v-if="shouldShowUpdateToReleaseButton"
                @click="$emit('confirmAndPinToRelease')"
              >
                <ion-spinner v-if="pinningToRelease" />
                <template v-else>
                  Update {{ details.release_track }} ({{ releaseTracks[details.release_track].version }})
                </template>
              </ion-button>
            </ion-item>

            <ion-item>
              Release track:
              <code>{{ details.release_track }}</code>
              <span
                v-if="releaseTracks[details.release_track]"
              >
                ({{ releaseTracks[details.release_track].version }})
              </span>
              <ion-button
                v-if="isAdmin"
                slot="end"
                @click="changeReleaseTrack"
              >
                Change
              </ion-button>
            </ion-item>

            <template v-if="!isAdmin && cachedBridge">
              <ion-item>
                IP address: {{ cachedBridge.ip_address }}
              </ion-item>
            </template>
          </ion-list>

          <h5>Connection to cloud</h5>
          <ion-list>
            <ion-item>
              Status:
              <span
                v-if="bridge.bridge.lastSeenInCloud"
                :class="{
                  online: wasRecentlyInCloud,
                  offline: !wasRecentlyInCloud
                }"
                class="last-seen-badge"
              >
                <template v-if="wasRecentlyInCloud">
                  Online
                </template>
                <template v-else>
                  Offline
                </template>
              </span>
              <ion-button
                slot="end"
                fill="outline"
                :disabled="syncingCloudStatus"
                @click="syncCloudStatus"
              >
                <ion-spinner v-if="syncingCloudStatus" />
                <template v-else>
                  <ion-icon
                    :src="i.refresh"
                  />
                </template>
              </ion-button>
            </ion-item>
            <ion-item>
              Last seen: {{ lastSeenInCloud }}
            </ion-item>
          </ion-list>

          <h5>API</h5>

          <ion-list>
            <ion-item>
              <span>Status:</span>
              <ion-badge
                v-if="isOnline"
                class="online"
              >
                Online
              </ion-badge>
              <ion-badge
                v-else
                class="offline"
              >
                Offline
              </ion-badge>
            </ion-item>

            <template v-if="bridge.cachedBridge">
              <ion-item>
                Last seen online: {{ bridge.cachedBridge.last_connectivity_event | datetime }}
              </ion-item>
            </template>
          </ion-list>

          <h5>VPN</h5>

          <ion-list>
            <ion-item>
              <span>Status:</span>
              <span
                :class="lastVPNStatus"
                class="last-seen-badge"
              >
                <template v-if="isVpnOnline">
                  Online
                </template>
                <template v-else>
                  Offline
                </template>
              </span>
              <ion-button
                slot="end"
                fill="outline"
                :disabled="testingVPN"
                @click="testVPN"
              >
                <ion-spinner v-if="testingVPN" />
                <template v-else>
                  <ion-icon
                    :src="i.refresh"
                  />
                </template>
              </ion-button>
            </ion-item>

            <template v-if="bridge.cachedBridge">
              <ion-item>
                Last seen online: {{ bridge.cachedBridge.last_vpn_event | datetime }}
              </ion-item>
            </template>
          </ion-list>

          <template
            v-if="isAdmin"
          >
            <h5>Details</h5>

            <ion-list>
              <ion-item>
                <span>UUID: </span>
                <code :title="details.balena_id">
                  {{ details.balena_id.substring(0, 7) }}
                </code>

                <ion-button
                  v-clipboard:copy="details.balena_id"
                  color="primary"
                  class="uuid-clipboard"
                  fill="outline"
                  size="small"
                >
                  <ion-icon
                    :src="i.clipboardOutline"
                    mode="ios"
                  />
                </ion-button>
                <ion-button
                  :href="`https://dashboard.balena-cloud.com/devices/${details.balena_id}`"
                  target="_blank"
                  color="primary"
                  class="uuid-clipboard"
                  fill="outline"
                  size="small"
                >
                  <ion-icon
                    :src="i.openOutline"
                    mode="ios"
                  />
                </ion-button>
              </ion-item>

              <template v-if="cachedBridge">
                <ion-item>
                  Bridge Type: {{ cachedBridge.device_type }}
                </ion-item>

                <ion-item>
                  OS: {{ cachedBridge.os_version }} / {{ cachedBridge.os_variant }}
                </ion-item>

                <ion-item>
                  IP address (internal): {{ cachedBridge.ip_address }}
                </ion-item>

                <ion-item>
                  IP address (public): {{ cachedBridge.public_address }}
                </ion-item>
              </template>
            </ion-list>

            <h5>Environment Variables</h5>

            <ion-list>
              <ion-item
                v-for="env in bridge.environmentVariables"
                :key="env.name"
              >
                {{ env.name }}: {{ env.value }}
              </ion-item>
            </ion-list>
          </template>

          <ion-item class="egro-header">
            <h5>e-Gro account details on bridge</h5>

            <ion-button
              slot="end"
              fill="outline"
              :disabled="syncing"
              @click="sync"
            >
              <ion-spinner v-if="syncing" />
              <template v-else>
                <ion-icon
                  :src="i.refresh"
                />
              </template>
            </ion-button>
          </ion-item>

          <ion-list>
            <ion-item>
              Facility name: {{ bridge.facilityName }}
            </ion-item>

            <template v-if="egroInfo && egroInfo.user">
              <ion-item>
                Account ID: {{ egroInfo.user.id }}
              </ion-item>

              <ion-item>
                Account email: {{ egroInfo.user.email }}
              </ion-item>

              <ion-item>
                Company ID: {{ egroInfo.user.companyId }}
              </ion-item>

              <ion-item>
                Operation/Greenhouse ID: {{ egroInfo.user.divisionId }}
              </ion-item>

              <ion-item>
                Grower Type: {{ egroInfo.user.growerType }}
              </ion-item>

              <ion-item>
                Updated at: {{ egroInfo.user.updatedAt | datetime }}
              </ion-item>
            </template>
            <template v-else>
              <ion-item>
                <em>Not connected to e-Gro</em>
              </ion-item>
            </template>
          </ion-list>
        </ion-col>

        <ion-col size="6">
          <template v-if="isAdmin && settings">
            <h5>Bridge Settings</h5>
            <p class="text-small">
              Loaded from Bridge directly via VPN
            </p>

            <ion-list>
              <ion-item>
                Recovery Email: {{ settings.recoveryEmail }}
                <ion-button
                  slot="end"
                  @click="changeRecoveryEmail"
                >
                  Change
                </ion-button>
              </ion-item>
              <ion-item>
                Facility Name: {{ settings.facilityName }}
              </ion-item>
              <ion-item>
                Grower Type: {{ settings.growerType }}
              </ion-item>
              <ion-item>
                Auto Update: {{ settings.autoUpdate }}
              </ion-item>
              <ion-item>
                Timezone: {{ settings.timezone }}
              </ion-item>
            </ion-list>
          </template>

          <h5>Notes</h5>

          <ion-textarea-vue
            v-model="details.notes"
            rows="8"
            color="dark"
          />
          <ion-button
            :disabled="saving"
            @click="save"
          >
            <ion-spinner v-if="saving" />
            <template v-else>
              Update
            </template>
          </ion-button>
        </ion-col>
      </ion-row>
    </ion-grid>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import regions from '../region'
import cloudRegions from '../cloudRegion';

import { IonTextareaVue, modalController } from '@modus/ionic-vue'
import ChangeReleaseTrackModal from "./ChangeReleaseTrackModal";
import ChangeRecoveryEmailModal from "./ChangeRecoveryEmailModal";
import moment from 'moment'

export default {
  components: {
    IonTextareaVue
  },
  props: {
    pinningToRelease: {
      type: Boolean,
    }
  },
  data() {
    return {
      saving: false,
      syncing: false,
      syncingCloudStatus: false,
      testingVPN: false,
      lastVPNStatus: 'unknown',
      serialNumber: this.$route.params.serialNumber
    }
  },
  computed: {
    ...mapGetters([
      'user',
      'isAdmin'
    ]),
    bridge() {
      return this.$store.getters.getBridgeBySerial(this.serialNumber)
    },
    cachedBridge() {
      return this.bridge.cachedBridge
    },
    details() {
      return this.bridge.bridge
    },
    settings() {
      return this.bridge.settings
    },
    egroInfo() {
      return this.bridge.egroInfo
    },
    isOnline() {
      return this.cachedBridge && this.cachedBridge.is_online
    },
    wasRecentlyInCloud(){
      if(!this.bridge.bridge.lastSeenInCloud) return false
      return moment().diff(this.bridge.bridge.lastSeenInCloud, 'minutes') < 30
    },
    lastSeenInCloud(){
      if(this.syncingCloudStatus) return '-'
      if(!this.bridge.bridge.lastSeenInCloud) return 'Never'
      return moment(this.bridge.bridge.lastSeenInCloud).fromNow()
    },
    isVpnOnline() {
      return this.lastVPNStatus === 'online'
    },
    region() {
      return regions[this.details.region]
    },
    cloudRegion() {
      return cloudRegions.getKey(this.details.cloud_region)
    },
    type() {
      return this.user.type
    },
    releaseTracks() {
      return this.$store.state.bridge.bridgeReleaseTracks
    },
    shouldShowUpdateToReleaseButton() {
      const updateAvailable = this.releaseTracks[this.details.release_track] &&
          this.bridge.commit.version !== this.releaseTracks[this.details.release_track].version
      const allowedToUpdate = this.isAdmin || this.details.release_track === 'production'
      return updateAvailable && allowedToUpdate
    }
  },
  mounted() {
    if (this.isAdmin) {
      this.$store.dispatch('fetchBridgeSettings', { serialNumber: this.serialNumber })
    }
    this.$store.dispatch('fetchLatestBridgeReleaseTracks')
    this.syncCloudStatus()
    this.testVPN()
  },
  methods: {
    async save() {
      this.saving = true

      return this.$store.dispatch('updateBridge', this.bridge)
        .finally(() => this.saving = false)
    },
    async sync() {
      const serialNumber = this.details.serial_number

      this.syncing = true

      return this.$store.dispatch('syncBridge', { serialNumber })
        .finally(() => this.syncing = false)
    },
    async syncCloudStatus() {
      this.syncingCloudStatus = true
      return this.$store.dispatch('fetchBridgeStatus', this.bridge)
        .finally(() => {
          this.syncingCloudStatus = false
          this.$forceUpdate()
        })
    },
    async testVPN() {
      this.testingVPN = true
      return this.$store.dispatch('fetchVpnBeacon',{ serialNumber:this.serialNumber })
        .then(() => {
          this.lastVPNStatus = 'online'
        })
        .catch(() =>{
          this.lastVPNStatus = 'offline'
        })
        .finally(() => {
          this.testingVPN = false
        })
    },
    async changeRecoveryEmail() {
      if (!this.isAdmin) return
      const modal = await modalController
        .create({
          component: ChangeRecoveryEmailModal,
          componentProps: {
            propsData: {
              serialNumber: this.serialNumber,
              currentRecoveryEmail: this.settings.recoveryEmail
            }
          }
        })
      await modal.present()
    },
    async changeReleaseTrack() {
      const modal = await modalController
        .create({
          component: ChangeReleaseTrackModal,
          componentProps: {
            propsData: {
              currentReleaseTrack: this.details.release_track
            }
          }
        })
      await modal.present()
      await modal.onDidDismiss()
      await this.sync()
    }
  }
}
</script>

<style scoped>
ion-grid {
  width:100%;
}

ion-textarea {
  border: 1px solid rgba(0, 0, 0, 0.13);
}

code, ion-badge {
  margin-left:5px;
}

.card-content-md h5 {
  font-size: 18px;
  margin-top:16px;
}

.egro-header {
  --padding-start:0;
  --border-style:none;
}

.egro-header h5 {
  color:#737373;
}

.last-seen-badge{
  border-radius: 4px;
  display: block;
  padding: 3px 4px 4px;
  margin-left: 5px;
  font-size: 13px;
  color:var(--ion-color-primary-contrast);
  background-color:var(--ion-color-primary);
  line-height: 1;
  font-weight: bold;
}
.last-seen-badge.online{
  background-color: var(--ion-color-success);
}
.last-seen-badge.offline{
  background-color: var(--ion-color-danger);
}
</style>

<style>
ion-tab.ion-page {
  position:static;
  contain: inherit;
}

.egro-header ion-spinner {
  width:12px;
}

</style>
